import React, { useEffect, useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridFilterCellProps,
  GridToolbar,
  isColumnMenuSortActive,
  isColumnMenuFilterActive,
} from '@progress/kendo-react-grid';
import { createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import { Button } from '@progress/kendo-react-buttons';
import '@progress/kendo-theme-bootstrap/dist/all.css';
import {
  ColumnMenu,
  CustomCell,
  HeaderCustomCell,
} from '../../ColumnMenus/ColumnMenu';
import StringResourceManager, { GetString } from '../../StringResourceManager';
import {
  IntlProvider,
  loadMessages,
  LocalizationProvider,
} from '@progress/kendo-react-intl';
import { LocaleInterface, locales } from '../../AlertsGrid/GridLocale';
import { DataResult, State, process } from '@progress/kendo-data-query';
import {
  DetailExpandDescriptor,
  setGroupIds,
} from '@progress/kendo-react-data-tools';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { ApplicationState } from '../../../store';
import CloseIcon from '@mui/icons-material/Close';
import {
  Upload,
  UploadOnAddEvent,
  UploadOnRemoveEvent,
} from '@progress/kendo-react-upload';
import {
  FirmwareFileAndChecksumMap,
  FirmwareInfo,
  MultipleFirmwareRecords,
} from '../../../Interfaces/Devices/FirmwareInfo';
import {
  getFirmwareInfo,
  addNewFirmwareInfo,
  updateFirmwareFile,
  removeFirmwareInfo,
  resetSuccessMessage,
  calculateChecksum,
  resetUpdateMessage,
  clearChecksum,
  resetDeleteMessage,
} from '../../../store/Devices/AssetStatesActions';
import { saveFirmwareGridState } from '../../../store/UserSettings/UserSettingsActions';
import { initialFirmwareGridDataState } from '../../../store/UserSettings/UserSettingsState';
import { AddNewFirmwareDialog } from './AddNewFirmwareDialog';
import { ConfirmDeleteFirmwareDialog } from './ConfirmDeleteFirmwareDialog';
import { FirmwareDetail } from './FirmwareDetail';
import { getLocalDateTime } from '../../../utils/utils';
import { Fade } from '@progress/kendo-react-animation';
import {
  Notification,
  NotificationGroup,
} from '@progress/kendo-react-notification';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button as DialogButton } from '@mui/material';
import { CustomDateFilterCell } from '../../CustomDateFilterCell';
import esMessages from '../../../utils/gridMessageKey.json';
import { de } from 'date-fns/locale';

loadMessages(esMessages, 'fr-CA');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    headerLabelGrid: {
      width: '90%',
      marginTop: 10,
      marginLeft: 10,
      marginBottom: 10,
    },
    toolbarButtonContainer: {
      display: 'grid',
    },
    toolbarButtonGrid: {
      display: 'flex',
    },
    actionCell: {
      display: 'flex',
      gap: theme.spacing(2),
      alignItems: 'center',
    },
    closeButton: {
      backgroundColor: 'transparent',
      color: theme.palette.error.main,
      border: 'none',
      '&:hover': {
        backgroundColor: theme.palette.error.light,
      },
    },
    bottomRight: {
      right: 0,
      bottom: 0,
      alignItems: 'flex-start',
      flexWrap: 'wrap-reverse',
    },
    send: {
      fontSize: '1.5rem',
    },
  })
);

const FirmwareGrid: React.FC = ({}): React.ReactElement => {
  const classes = useStyles();
  const dispatch: Dispatch<any> = useDispatch();

  const COLUMN_TITLE_DEVICE_CATEGORY = GetString(
    'COLUMN_TITLE_DEVICE_CATEGORY'
  );
  const COLUMN_TITLE_DEVICE_MODEL = GetString('COLUMN_TITLE_DEVICE_MODEL');
  const COLUMN_TITLE_REGION_INFO = GetString('COLUMN_TITLE_REGION_INFO');
  const COLUMN_TITLE_FIRMWARE_FILE = GetString('COLUMN_TITLE_FIRMWARE_FILE');
  const COLUMN_TITLE_FIRMWARE_IMPORT_DATE = GetString(
    'COLUMN_TITLE_FIRMWARE_IMPORT_DATE'
  );
  const YES = GetString('YES');
  const NO = GetString('NO');
  const FIRMWARE_UPDATE_CONFIRMATION_TITLE = GetString(
    'FIRMWARE_UPDATE_CONFIRMATION_TITLE'
  );
  const FIRMWARE_UPDATE_CONFIRMATION_MESSAGE = GetString(
    'FIRMWARE_UPDATE_CONFIRMATION_MESSAGE'
  );
  const COLUMN_TITLE_NEW_FIRMWARE_CHECKSUM = GetString(
    'COLUMN_TITLE_NEW_FIRMWARE_CHECKSUM'
  );
  const COLUMN_TITLE_NEW_FIRMWARE_FILE = GetString(
    'COLUMN_TITLE_NEW_FIRMWARE_FILE'
  );
  const ADD_FIRMWARE_FILE_SUCCESS_MESSAGE = GetString(
    'ADD_FIRMWARE_FILE_SUCCESS_MESSAGE'
  );
  const ADD_FIRMWARE_FILE_FAILED_MESSAGE = GetString(
    'ADD_FIRMWARE_FILE_FAILED_MESSAGE'
  );

  const validExtensions: string[] = ['.zip', '.reg', '.rf', '.dat'];

  const [successMessage, displaySuccessMessage] = React.useState(false);
  const [updateSuccessMessage, displayUpdateSuccessMessage] =
    React.useState(false);
  const [deleteSuccessMessage, displayDeleteSuccessMessage] =
    React.useState(false);

  const [showConfirmUpdateDialog, setShowConfirmUpdateDialog] =
    React.useState(false);

  const [fileName, setFileName] = React.useState('');
  const [selectedFiles, setSelectedFiles] = React.useState<Map<any, any[]>>(
    new Map()
  );

  const [tempFileData, setTempFileData] = React.useState<FormData>();
  const [selectedFileData, setSelectedFileData] = React.useState<FormData>();
  const [waitingForChecksum, setWaitingForChecksum] = React.useState(false);
  const [selectedRowId, setSelectedRowId] = React.useState<number>();

  const fileChecksumMap: FirmwareFileAndChecksumMap[] = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.fileChecksumMap
  );
  const confirmationMessage: string = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.confirmationMessage
  );

  const registrationStatus: string = useSelector(
    (state: ApplicationState) =>
      state.assetStatusesReducer.registrationSuccessMessage
  );
  const updateStatus: string = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.updateSuccessMessage
  );
  const deleteStatus: string = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.deleteSuccessMessage
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const dataState: State = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.firmwareGridDataState
  );
  const firmwareInfo: FirmwareInfo[] = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.firmwareInfo
  );
  const filesUploaded: string[] = useSelector(
    (state: ApplicationState) => state.assetStatusesReducer.filesUploaded
  );

  const processWithGroups = (data: any[], dataState: State): DataResult => {
    if (dataState === undefined) {
      dataState = initialFirmwareGridDataState;
    }

    const newDataState: DataResult = process(data, dataState);
    setGroupIds({ data: newDataState.data, group: dataState.group });
    return newDataState;
  };

  const [resultState, setResultState] = React.useState<DataResult>(
    processWithGroups(firmwareInfo, dataState)
  );

  const [showConfirmDeleteDialog, setShowConfirmDeleteDialog] = useState(false);
  const [firmwareInfoToDelete, setFirmwareInfoToDelete] =
    useState<FirmwareInfo>();

  const [firmwareInfoToSave, setFirmwareInfoToSave] =
    React.useState<MultipleFirmwareRecords>();

  const [showAddNewFirmwareDialog, setShowAddNewFirmwareDialog] =
    useState<boolean>(false);

  var newData = resultState.data;
  const [detailExpand, setDetailExpand] =
    React.useState<DetailExpandDescriptor>({ [newData[1]]: true });

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    const newDataState = processWithGroups(firmwareInfo, event.dataState);
    saveFirmwareGridState(dispatch, event.dataState);
    setResultState(newDataState);
  };

  useEffect(() => {
    getFirmwareInfo(dispatch);
  }, []);

  useEffect(() => {
    setResultState(processWithGroups(firmwareInfo, dataState));
  }, [firmwareInfo]);

  useEffect(() => {
    if (waitingForChecksum && fileChecksumMap.length !== 0 && tempFileData) {
      tempFileData.append('newchecksum', fileChecksumMap[0].FileChecksum);
      setSelectedFileData(tempFileData);
      setShowConfirmUpdateDialog(!showConfirmUpdateDialog);
      setWaitingForChecksum(false);
    }
  }, [fileChecksumMap, waitingForChecksum]);

  const ActionCell = (props: GridCellProps) => {
    const { dataItem } = props;
    if (dataItem.hasOwnProperty('items')) {
      return null;
    }

    const validateSelectedFiles = (event: UploadOnAddEvent) => {
      const invalidFiles = event.newState.filter(file => {
        const lowerName = file.name.toLowerCase();
        return !validExtensions.some(ext => lowerName.endsWith(ext));
      });
      setSelectedFiles(prev => {
        const newMap = new Map(prev);
        newMap.set(dataItem.ID, event.newState);
        return newMap;
      });
      if (invalidFiles.length > 0) return;

      setFileName(event.newState[0].name);
      setSelectedRowId(dataItem.ID);
      setWaitingForChecksum(true);
      event.newState.forEach(file => {
        const formData = new FormData();
        const rawFile = file.getRawFile ? file.getRawFile() : undefined;
        if (rawFile) {
          formData.append('id', dataItem.ID);
          formData.append('file', rawFile);
          formData.append('oldfilepath', dataItem.FilePath);
          formData.append('oldchecksum', dataItem.FileChecksum);
          setTempFileData(formData);
          calculateChecksum(dispatch, formData);
          setSelectedFiles(prev => {
            const newMap = new Map(prev);
            newMap.set(dataItem.ID, event.newState);
            return newMap;
          });
        }
      });
    };

    const removeSelectedFiles = (event: UploadOnRemoveEvent) => {
      setSelectedFiles(prev => {
        const newMap = new Map(prev);
        newMap.set(dataItem.ID, event.newState);
        return newMap;
      });
    };

    const removeFirmwareInfo = () => {
      setFirmwareInfoToDelete(dataItem);
      setShowConfirmDeleteDialog(!showConfirmDeleteDialog);
    };

    return (
      <td className={classes.actionCell}>
        <Upload
          batch={false}
          restrictions={{
            allowedExtensions: validExtensions,
          }}
          files={selectedFiles.get(dataItem.ID)}
          autoUpload={false}
          multiple={false}
          showActionButtons={false}
          onAdd={validateSelectedFiles}
          onRemove={removeSelectedFiles}
        />
        <Button
          themeColor="error"
          type="button"
          className={classes.closeButton}
          onClick={removeFirmwareInfo}
        >
          <CloseIcon />
        </Button>
      </td>
    );
  };

  const DateTimeCell = (props: GridCellProps) => {
    const dataItem = props.dataItem;
    if (dataItem?.ImportDate && dataItem.ImportDate != '0001-01-01T00:00:00') {
      return (
        <td style={{ fontSize: '0.9rem' }}>
          {getLocalDateTime(dataItem.ImportDate)}
        </td>
      );
    }
    return <td></td>;
  };

  const dateFilterCell = (props: GridFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="ImportDate"
      />
    );
  };

  const toggleConfirmDeleteFirmwareDialog = () => {
    setShowConfirmDeleteDialog(!showConfirmDeleteDialog);
  };
  const toggleConfirmUpdateFirmwareDialog = () => {
    if (selectedRowId !== null) {
      setSelectedFiles(prev => {
        const newMap = new Map(prev);
        newMap.delete(selectedRowId);
        return newMap;
      });
    }
    clearChecksum(dispatch);
    setShowConfirmUpdateDialog(!showConfirmUpdateDialog);
  };

  const deleteFirmwareInfo = () => {
    if (firmwareInfoToDelete)
      removeFirmwareInfo(dispatch, firmwareInfoToDelete).then(() => {
        displayDeleteSuccessMessage(true);
        getFirmwareInfo(dispatch);
      });
    toggleConfirmDeleteFirmwareDialog();
  };

  const uploadFirmwareInfo = () => {
    if (selectedFileData)
      updateFirmwareFile(dispatch, selectedFileData).then(() => {
        displayUpdateSuccessMessage(true);
        getFirmwareInfo(dispatch);
        clearChecksum(dispatch);
      });
    toggleConfirmUpdateFirmwareDialog();
  };

  const saveAddNewFirmware = (firmwareInfo?: MultipleFirmwareRecords) => {
    if (firmwareInfo) setFirmwareInfoToSave(firmwareInfo);
    hideAddAddNewFirmwareDialog();
    clearChecksum(dispatch);
  };

  useEffect(() => {
    if (registrationStatus === 'success') {
      if (firmwareInfoToSave) {
        if (filesUploaded.length !== 0) {
          const filteredMap =
            firmwareInfoToSave.FirmwareFileAndChecksumMap.filter(item =>
              filesUploaded.includes(item.FileName)
            );
          const updatedFirmwareInfo = {
            ...firmwareInfoToSave,
            FirmwareFileAndChecksumMap: filteredMap,
          };
          addNewFirmwareInfo(dispatch, updatedFirmwareInfo).then(() => {
            displaySuccessMessage(true);
            getFirmwareInfo(dispatch);
          });
        }
      }
    }
    displaySuccessMessage(true);
  }, [registrationStatus]);

  useEffect(() => {
    let timeoutId: any;
    if (successMessage) {
      timeoutId = setTimeout(() => {
        displaySuccessMessage(false);
        resetSuccessMessage(dispatch);
      }, 3000);
    } else if (updateSuccessMessage) {
      timeoutId = setTimeout(() => {
        displayUpdateSuccessMessage(false);
        resetUpdateMessage(dispatch);
      }, 3000);
    } else if (deleteSuccessMessage) {
      timeoutId = setTimeout(() => {
        displayDeleteSuccessMessage(false);
        resetDeleteMessage(dispatch);
      }, 3000);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [successMessage, updateSuccessMessage, deleteSuccessMessage]);

  const hideAddAddNewFirmwareDialog = () => {
    setShowAddNewFirmwareDialog(false);
  };

  const onClickAddNewFirmware = () => {
    setShowAddNewFirmwareDialog(true);
  };

  const columnProps = (field: string) => {
    return {
      field: field,
      columnMenu: ColumnMenu,
      headerClassName: isColumnActive(field, dataState) ? 'active' : '',
    };
  };

  const isColumnActive = (field: string, dataState: State) => {
    return (
      isColumnMenuFilterActive(field, dataState.filter) ||
      isColumnMenuSortActive(field, dataState.sort)
    );
  };

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  return (
    <React.Fragment>
      <LocalizationProvider language={currentLocale.language}>
        <IntlProvider locale={currentLocale.locale}>
          <div className={classes.headerLabelGrid}>
            <StringResourceManager
              variant="body1"
              style="dialogLabelBold"
              stringKey={'FIRMWARE_MENU_HEADER'}
              gutterBottom={true}
            />
          </div>
          <Grid
            style={{ height: '80vh' }}
            resizable={true}
            sortable={true}
            filterable={true}
            pageable={{
              buttonCount: 4,
              pageSizes: [50, 100, 500, 1000, 5000],
            }}
            data={resultState}
            {...dataState}
            detail={FirmwareDetail}
            groupable={true}
            reorderable={true}
            fixedScroll={false}
            onDataStateChange={dataStateChange}
            dataItemKey="ID"
            defaultDetailExpand={detailExpand}
            rowHeight={50}
            cells={{
              headerCell: HeaderCustomCell,
              data: CustomCell,
            }}
          >
            <GridToolbar>
              <div className={classes.toolbarButtonContainer}>
                <div className={classes.toolbarButtonGrid}>
                  <Button onClick={onClickAddNewFirmware}>
                    <StringResourceManager stringKey="FIRMWARE_MENU_ADD_NEW_FIRMWARE" />
                  </Button>
                </div>
              </div>
            </GridToolbar>
            <GridColumn
              {...columnProps('DeviceCategory')}
              title={COLUMN_TITLE_DEVICE_CATEGORY}
              width="165px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('DeviceModel')}
              title={COLUMN_TITLE_DEVICE_MODEL}
              width="155px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('RegionInfo')}
              title={COLUMN_TITLE_REGION_INFO}
              width="140px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('FileName')}
              title={COLUMN_TITLE_FIRMWARE_FILE}
              width="425px"
              columnMenu={ColumnMenu}
              filterable={false}
            />
            <GridColumn
              {...columnProps('ImportDate')}
              title={COLUMN_TITLE_FIRMWARE_IMPORT_DATE}
              cells={{ data: DateTimeCell, filterCell: dateFilterCell }}
              width="250px"
            />
            <GridColumn
              cells={{ data: ActionCell }}
              title={GetString('COLUMN_TITLE_ACTIONS')}
              width="470px"
              filterable={false}
            />
          </Grid>

          {showAddNewFirmwareDialog && (
            <AddNewFirmwareDialog
              onSave={saveAddNewFirmware}
              onCancel={hideAddAddNewFirmwareDialog}
            />
          )}
          {showConfirmDeleteDialog && (
            <ConfirmDeleteFirmwareDialog
              onConfirm={deleteFirmwareInfo}
              onCancel={toggleConfirmDeleteFirmwareDialog}
              fileName={firmwareInfoToDelete?.FileName}
            />
          )}
          {showConfirmUpdateDialog && (
            <Dialog
              onClose={toggleConfirmUpdateFirmwareDialog}
              title={FIRMWARE_UPDATE_CONFIRMATION_TITLE}
            >
              <Typography variant="body1">
                {confirmationMessage === 'add' &&
                  FIRMWARE_UPDATE_CONFIRMATION_MESSAGE}
              </Typography>
              <div>
                <section style={{ float: 'left' }}>
                  <p>
                    <strong>{COLUMN_TITLE_NEW_FIRMWARE_CHECKSUM}:</strong>{' '}
                    {fileChecksumMap[0].FileChecksum}
                    <br></br>
                    <strong>{COLUMN_TITLE_NEW_FIRMWARE_FILE}:</strong>{' '}
                    {fileName}
                  </p>
                </section>
              </div>
              <DialogActionsBar layout="center">
                <DialogButton
                  color="primary"
                  variant="contained"
                  onClick={uploadFirmwareInfo}
                  disabled={fileChecksumMap.length === 0}
                >
                  {YES}
                </DialogButton>
                <DialogButton
                  color="primary"
                  variant="contained"
                  onClick={toggleConfirmUpdateFirmwareDialog}
                >
                  {NO}
                </DialogButton>
              </DialogActionsBar>
            </Dialog>
          )}

          <Fade>
            <NotificationGroup className={classes.bottomRight}>
              {successMessage && registrationStatus && (
                <Notification
                  className={classes.send}
                  type={{
                    style:
                      registrationStatus === 'success' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <Typography variant="body1">
                      {registrationStatus === 'success'
                        ? `${filesUploaded.length} ${ADD_FIRMWARE_FILE_SUCCESS_MESSAGE}`
                        : ADD_FIRMWARE_FILE_FAILED_MESSAGE}
                    </Typography>
                  }
                </Notification>
              )}
              {updateSuccessMessage && updateStatus && (
                <Notification
                  className={classes.send}
                  type={{
                    style: updateStatus === 'success' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        updateStatus === 'success'
                          ? 'UPDATE_FIRMWARE_FILE_SUCCESS_MESSAGE'
                          : 'UPDATE_FIRMWARE_FILE_FAILED_MESSAGE'
                      }
                    />
                  }
                </Notification>
              )}
              {deleteSuccessMessage && deleteStatus && (
                <Notification
                  className={classes.send}
                  type={{
                    style: deleteStatus === 'success' ? 'success' : 'error',
                    icon: true,
                  }}
                  closable={true}
                >
                  {
                    <StringResourceManager
                      stringKey={
                        deleteStatus === 'success'
                          ? 'DELETE_FIRMWARE_FILE_SUCCESS_MESSAGE'
                          : 'DELETE_FIRMWARE_FILE_FAILED_MESSAGE'
                      }
                    />
                  }
                </Notification>
              )}
            </NotificationGroup>
          </Fade>
        </IntlProvider>
      </LocalizationProvider>
    </React.Fragment>
  );
};
export default FirmwareGrid;
